// useScanStream — subscribe to a WS topic and surface incoming scan events.
//
//   const { lastEvent, connected } = useScanStream('asset:E200-0001-A001');
//   const { lastEvent } = useScanStream('audit:42');
//   const { lastEvent } = useScanStream('scans');     // global feed
//
// Auto-reconnects with exponential backoff. Closes on unmount.

import { useEffect, useRef, useState } from 'react';

const buildWsUrl = (token) => {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  // Frontend dev runs on :3000 with proxy → use backend host directly
  // In dev (localhost:3000), CRA's proxy doesn't handle WS upgrade reliably,
  // so we hit :5001 explicitly. In prod the same origin works.
  let host = window.location.host;
  let base = process.env.PUBLIC_URL || '';
  if (host.endsWith(':3000')) { host = host.replace(':3000', ':5001'); base = ''; } // dev hits backend directly, no nginx prefix
  return `${proto}://${host}${base}/ws?token=${encodeURIComponent(token)}`;
};

export function useScanStream(topic) {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const closingRef = useRef(false);

  useEffect(() => {
    if (!topic) return;
    closingRef.current = false;

    const open = () => {
      const token = localStorage.getItem('identium_token');
      if (!token) return; // not signed in yet

      const ws = new WebSocket(buildWsUrl(token));
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        setConnected(true);
        ws.send(JSON.stringify({ type: 'subscribe', topic }));
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'event' && msg.topic === topic) {
            setLastEvent({ ...msg.payload, _ts: Date.now() });
          }
        } catch {}
      };
      ws.onerror = () => {};
      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        if (closingRef.current) return;
        const delay = Math.min(15000, 500 * Math.pow(2, retryRef.current));
        retryRef.current += 1;
        setTimeout(open, delay);
      };
    };

    open();
    return () => {
      closingRef.current = true;
      if (wsRef.current) {
        try { wsRef.current.close(); } catch {}
        wsRef.current = null;
      }
    };
  }, [topic]);

  return { connected, lastEvent };
}
